import cx from 'classnames'
import NextLink from 'next/link'
import { useCallback, useContext, useMemo, useState } from 'react'

import { SanityProductFragment } from '@data/sanity/queries/types/product'
import { SanityPrintShopBase } from '@data/sanity/queries/types/shop'
import { SanityPrintConfiguratorStrings } from '@data/sanity/queries/types/strings'
import { CartContext } from '@lib/cart'
import { ProductVariantSelectionContext } from '@lib/product-variant-selection-context'
import { getPageUrl, PageType, PageUrlOptions } from '@lib/routes'
import { productVariantIdAttributeKey } from '@lib/shopify/cart'
import { StringsContext } from '@lib/strings-context'

import Alert from '@components/alert'
import Icon from '@components/icon'
import ComplexPortableText from '@components/portable-text/complex'

interface PrintProductActionsProps {
  product: SanityProductFragment
  printShop?: SanityPrintShopBase
  printConfiguratorStrings: SanityPrintConfiguratorStrings
}

/**
 * Component that shows actions for print products based on whether a shop is selected.
 */
const PrintProductActions = ({
  printShop,
  printConfiguratorStrings,
  product,
}: PrintProductActionsProps) => {
  const { cart, isCartProductAdding, addItemsToCart } = useContext(CartContext)
  const { selectedVariant, selectedOptions } = useContext(
    ProductVariantSelectionContext,
  )
  const strings = useContext(StringsContext)

  const [quantity, setQuantity] = useState(1)
  const [isAddToCartError, setIsAddToCartError] = useState(false)

  const printShopProductConfigurationUrl = useMemo(() => {
    if (!printShop) {
      return ''
    }

    const urlOptions: PageUrlOptions = {
      parameters: {
        variant:
          product.surfaceOption && selectedVariant?.id
            ? `${selectedVariant.id}`
            : '',
      },
    }

    return getPageUrl(
      PageType.SHOP_PRODUCT_PRINT,
      [printShop.slug, product.slug],
      urlOptions,
    )
  }, [selectedVariant, printShop, product])

  const createShopUrl = useMemo(() => {
    return getPageUrl(PageType.CREATE_SHOP_PAGE)
  }, [])

  const isOptionSelectionValid = useMemo(() => {
    if (!selectedVariant) {
      return false
    }

    // Skip validation if variant has no options to choose
    if (selectedVariant.options.length === 0) {
      return true
    }

    // Check if there exists a variant which has all of its options in the selected options array
    return !!product.variants?.some((variant) => {
      return variant.options.every((variantOption) => {
        return !!selectedOptions?.some((selectedOption) => {
          return (
            variantOption.name === selectedOption.name &&
            variantOption.value === selectedOption.value
          )
        })
      })
    })
  }, [product, selectedVariant, selectedOptions])

  const title = printConfiguratorStrings.printConfiguratorTeamShopTitle
  const description =
    printConfiguratorStrings.printConfiguratorTeamShopDescription
  const configurePrintButtonText =
    printConfiguratorStrings.printConfiguratorTeamShopButtonText

  const handleAddToCart = useCallback(async () => {
    if (
      !selectedVariant ||
      !selectedVariant.inStock ||
      !isOptionSelectionValid
    ) {
      return
    }

    setIsAddToCartError(false)

    const feeLineItems =
      selectedVariant.fees?.map((fee) => ({
        id: fee.variantID,
        quantity,
        attributes: [
          {
            key: `_${productVariantIdAttributeKey}`,
            value: selectedVariant.id.toString(),
          },
        ],
      })) ?? []

    const isSuccessful = await addItemsToCart(async () => {
      return [
        {
          id: selectedVariant.id,
          quantity,
        },
        ...feeLineItems,
      ]
    })

    setIsAddToCartError(!isSuccessful)
  }, [addItemsToCart, isOptionSelectionValid, quantity, selectedVariant])

  return (
    <>
      <div className="flex flex-col gap-y-1">
        <p className="is-h3 leading-tight font-semibold text-[22px]">{title}</p>
        <ComplexPortableText content={description} className="rc text-base" />
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <NextLink
          href={printShop ? printShopProductConfigurationUrl : createShopUrl}
          className="w-full xs:w-auto"
        >
          {/* Configure print button with custom styling */}
          <button
            disabled={!selectedVariant?.inStock || !isOptionSelectionValid}
            className={cx(
              'flex items-center justify-center gap-x-2.5',
              'w-full xs:w-auto pl-8 pr-5 py-3.5',
              'border rounded-full border-yellow hover:border-yellow-dark',
              'leading-tight font-medium text-base text-center text-black',
              'bg-yellow hover:bg-yellow-dark',
              'transition-all duration-300 disabled:opacity-50 whitespace-nowrap',
            )}
          >
            {printShop ? configurePrintButtonText : strings.buttonCreateShop}
            <Icon name="ArrowRight" />
          </button>
        </NextLink>

        {/* Add to cart button with custom styling */}
        <button
          disabled={
            !selectedVariant?.inStock ||
            !isOptionSelectionValid ||
            !cart ||
            isCartProductAdding
          }
          onClick={handleAddToCart}
          className={cx(
            'inline-flex items-center justify-center gap-x-2.5',
            'w-full xs:w-auto pl-8 pr-5 py-3.5',
            'border rounded-full border-btn-secondary-border hover:border-btn-secondary-border-hover',
            'leading-tight font-medium text-base text-center text-btn-secondary-text hover:text-btn-secondary-text-hover',
            'bg-btn-secondary-bg hover:bg-btn-secondary-bg-hover',
            'transition-all duration-300 disabled:opacity-50 whitespace-nowrap',
          )}
        >
          {!cart && <>{strings.buttonUnavailable}</>}
          {cart && isCartProductAdding && <>{strings.buttonAdding}</>}
          {cart && !isCartProductAdding && (
            <>{strings.buttonAddToCartPrintProduct}</>
          )}
          <Icon name="Cart" />
        </button>
      </div>

      {isAddToCartError && (
        <Alert error>
          <ComplexPortableText
            content={strings.cartAddToCartErrorMessage}
            className="rc rc-error"
          />
        </Alert>
      )}
    </>
  )
}

export default PrintProductActions
